import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, X } from "lucide-react";
import api from "../lib/api";

// Human-friendly labels for known Gravity Forms — falls back to
// "Form <id>" for anything not in this map so a newly-added form still
// surfaces on the banner without a code change.
const FORM_LABELS = {
  1: "General Contact (Form 1)",
  17: "Franchise Enquiry — long (Form 17)",
  32: "Licence Enquiry (Form 32)",
  33: "Franchise Enquiry — popup (Form 33)",
};

function formatRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * BackfillHealthBanner — polls /api/intake/backfill/health every 60s and
 * surfaces a per-form warning when the Gravity Forms REST backfill is
 * degraded or failing. Silent when everything is healthy (or when no
 * cycle has run yet, e.g. after a fresh pod boot).
 *
 * Placement: currently rendered at the top of ContactsPage (which owns
 * both the Sales Pipeline and Franchise Contacts tabs). One instance
 * covers both requested locations.
 */
export default function BackfillHealthBanner() {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await api.get("/intake/backfill/health");
      setHealth(data);
    } catch {
      // Silent — admin banner shouldn't error out the whole page.
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  if (!health || dismissed) return null;
  const overall = health.overall_status || "unknown";
  if (overall === "healthy" || overall === "unknown") return null;

  const failingForms = (health.per_form || []).filter((f) =>
    ["degraded", "failing"].includes(f.status)
  );
  if (failingForms.length === 0) return null;

  const isFailing = overall === "failing";
  const paletteBg = isFailing ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300";
  const paletteText = isFailing ? "text-red-900" : "text-amber-900";
  const paletteAccent = isFailing ? "text-red-700" : "text-amber-700";
  const paletteIcon = isFailing ? "text-red-600" : "text-amber-600";

  return (
    <div
      className={`border-b ${paletteBg} ${paletteText}`}
      data-testid="backfill-health-banner"
      data-status={overall}
    >
      <div className="px-8 py-3 flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${paletteIcon}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold">
              Gravity Forms sync · {overall}
            </div>
            <div className="text-sm">
              {isFailing
                ? `${failingForms.length} form${failingForms.length === 1 ? "" : "s"} not syncing from Gravity Forms — new submissions to affected forms may be delayed until this is resolved.`
                : `${failingForms.length} form${failingForms.length === 1 ? "" : "s"} had a recent fetch error — will retry automatically on the next cycle.`}
            </div>
          </div>

          {expanded && (
            <div className="mt-3 space-y-2" data-testid="backfill-health-details">
              {failingForms.map((f) => {
                const label = FORM_LABELS[f.form_id] || `Form ${f.form_id}`;
                const badgeCls =
                  f.status === "failing"
                    ? "bg-red-600 text-white"
                    : "bg-amber-500 text-white";
                return (
                  <div
                    key={f.form_id}
                    className="rounded-lg border border-white/60 bg-white/70 backdrop-blur-sm px-3 py-2.5"
                    data-testid={`backfill-form-${f.form_id}`}
                  >
                    <div className="flex items-start gap-2 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${badgeCls}`}
                      >
                        {f.status}
                      </span>
                      <span className="text-sm font-semibold">{label}</span>
                      <span className={`text-xs ${paletteAccent}`}>
                        {f.consecutive_failures} consecutive failure
                        {f.consecutive_failures === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className={`mt-1.5 text-xs ${paletteAccent} space-y-0.5`}>
                      <div>
                        <span className="font-semibold">Last success:</span>{" "}
                        {formatRelative(f.last_success_at)}
                        {f.last_success_at && (
                          <span className="opacity-60 ml-1">
                            ({new Date(f.last_success_at).toLocaleString()})
                          </span>
                        )}
                      </div>
                      {f.last_error && (
                        <div className="break-words">
                          <span className="font-semibold">Latest error:</span>{" "}
                          <code className="font-mono text-[11px]">
                            {f.last_error}
                          </code>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className={`text-xs ${paletteAccent} pt-1`}>
                Runbook: unblock WordPress <code>/wp-json/gf/v2</code> — see the
                WP 403 guide in <code>/memory/wp_403_unblock_guide.md</code>.
                Alerts to <code>admin@creativemojo.co.uk</code> fire on 2+
                consecutive failures per form.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded hover:bg-white/40 ${paletteAccent}`}
            data-testid="backfill-health-toggle"
          >
            {expanded ? "Hide" : "Details"}
          </button>
          <button
            type="button"
            onClick={fetchHealth}
            className={`p-1 rounded hover:bg-white/40 ${paletteAccent}`}
            title="Refresh health status"
            data-testid="backfill-health-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className={`p-1 rounded hover:bg-white/40 ${paletteAccent}`}
            title="Dismiss until next page load"
            data-testid="backfill-health-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export { BackfillHealthBanner };

// Optional secondary export — a compact single-line pill that shows
// green when healthy, so admins get positive confirmation the safety
// net is working. Not currently rendered (parent components can opt
// in if useful in future).
export function BackfillHealthPill() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/intake/backfill/health");
        if (!cancelled) setHealth(data);
      } catch {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  if (!health) return null;
  const s = health.overall_status;
  if (s === "healthy") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
        data-testid="backfill-health-pill"
        data-status="healthy"
      >
        <CheckCircle2 className="w-3 h-3" /> GF sync OK
      </span>
    );
  }
  return null;
}
